import { Component, OnDestroy, OnInit } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import {
  ActivateBlockAction,
  COMPANY_LOGO_SIZE,
  selectSelectedSurvey,
  SetLanguageAction,
  Survey,
  SurveyConfigService,
  SurveyFacade,
} from '@clients/member-direct/surveys';
import {
  ILanguage,
  TranslationService,
} from '@clients/member-direct/translation';
import {
  DROPDOWN_POSITION_ENUM,
  IDropdownData,
} from '@clients/shared/common-forms';
import { FLEX_LAYOUT_ENUM } from '@clients/shared/layout-grid';
import {
  selectApplicationLanguage,
  selectIsMovePage,
  selectIsNextGen,
  selectNavigationQuote,
  selectTranslatedLogo,
} from '../../+state';
import { SetAppLanguageAction } from '../../+state/config/config.actions';
import { IQuoteSchema } from '../../+state/config/config.interfaces';
import { memberDirectFeatureLocalePrefix } from '../../locale/locale';
import { ActivatedRoute } from '@angular/router';
import { BlocksFacade } from 'libs/member-direct/surveys/src/lib/+state/blocks/blocks.facade';
import { ResetResponsesAction } from 'libs/member-direct/surveys/src/lib/+state/responses/responses.actions';

@Component({
  selector: 'member-direct-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  public survey: Observable<Survey>;
  public logo: Observable<string>;
  public logoFromConfig: string;
  public miniSiteLogoFromConfig: string;
  public language: Observable<string>;
  public logoAltText: string;
  public quote: Observable<IQuoteSchema>;
  public isMobile: boolean;
  public languageDropdownData: Array<IDropdownData>;
  public dropdownPosition: DROPDOWN_POSITION_ENUM = DROPDOWN_POSITION_ENUM.TOP;
  public LAYOUT = FLEX_LAYOUT_ENUM;
  public DROPDOWN_POSITION_ENUM = DROPDOWN_POSITION_ENUM;
  public mobileMenuOpen: boolean;
  public mobileMenuInstructions =
    'This menu tracks your progress. After completing a section, you can revisit it by using this menu.';

  public shouldShowNameInSidebar = false;
  public shouldShowSidebarBlockNavigation = false;
  public logoSize = COMPANY_LOGO_SIZE.small;
  public nextGen: boolean;
  public isMovePage: boolean;

  private destroyed$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private store: Store<any>,
    private route: ActivatedRoute,
    private blocksFacade: BlocksFacade,
    private breakpointObserver: BreakpointObserver,
    public surveyConfigService: SurveyConfigService,
    public translationService: TranslationService,
    public surveyFacade: SurveyFacade
  ) {
    this.setupBreakpointObserver();
  }

  ngOnInit(): void {
    this.survey = this.store.pipe(select(selectSelectedSurvey));
    this.logo = this.store.pipe(select(selectTranslatedLogo));
    this.language = this.store.pipe(select(selectApplicationLanguage));
    this.quote = this.store.pipe(select(selectNavigationQuote));

    this.store.pipe(select(selectIsMovePage), takeUntil(this.destroyed$)).subscribe((isMove) => {
      this.isMovePage = isMove;
      this.nextGen = isMove;
    });
    /**
     * Currently the new UI is only for move pages
     * for the other surveys pages, we will keep the old UI
     */

    this.language
      .pipe(takeUntil(this.destroyed$))
      .subscribe((language: string) => {
        this.updateLogoAltText(language);
      });

    this.surveyConfigService.surveyConfigUpdated
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.updateAvailableLanguages();
        this.updateLogoAltText(this.surveyFacade.getSurveyLanguage());
        this.logoFromConfig = this.surveyConfigService.getLogo();
        this.miniSiteLogoFromConfig = this.surveyConfigService.getMiniSiteLogo();
        this.shouldShowNameInSidebar =
          this.surveyConfigService.shouldShowNameInSidebar();
        this.shouldShowSidebarBlockNavigation =
          this.surveyConfigService.shouldShowSidebarBlockNavigation();
        this.logoSize = this.surveyConfigService.getSideBarCompanyLogoSize();
      });

    /**
     * Subscribes to the route's query parameters and performs actions based on the presence and value of the `block` parameter.
     *
     * - If the `block` query parameter exists and differs from the currently active block, dispatches an `ActivateBlockAction`.
     * - If the `block` query parameter is absent but an active block exists, dispatches a `ResetResponsesAction`.
     *
     * The subscription is automatically unsubscribed when the `destroyed$` observable emits (typically on component destruction).
     *
     */
    this.route.queryParams
      .pipe(takeUntil(this.destroyed$))
      .subscribe((params) => {
        const blockId = params['blockId'];
        const active = this.blocksFacade.getActiveId();
        if (blockId && blockId !== active && active !== null) {
          this.store.dispatch(new ActivateBlockAction(blockId));
        } else if (!blockId && active) {
          this.store.dispatch(new ResetResponsesAction());
        }
      });
  }

  private setupBreakpointObserver(): void {
    const CustomBreakpoints = {
      Mobile: '(max-width: 768px)',
      Desktop: '(min-width: 769px)',
    };
    this.breakpointObserver
      .observe([CustomBreakpoints.Mobile])
      .pipe(takeUntil(this.destroyed$))
      .subscribe((state) => {
        this.isMobile = state.matches;
        this.updateLayoutSettings();
      });
  }

  private updateLayoutSettings(): void {
    this.dropdownPosition = this.isMobile
      ? DROPDOWN_POSITION_ENUM.BOTTOM
      : DROPDOWN_POSITION_ENUM.TOP;
  }

  onChangeHandler(value: string): void {
    this.store.dispatch(new SetLanguageAction(value));
    this.store.dispatch(new SetAppLanguageAction(value));
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public closeMobileMenu(): void {
    this.mobileMenuOpen = false;
  }

  private updateAvailableLanguages(): void {
    this.languageDropdownData = this.surveyConfigService
      .getAvailableLanguages()
      .map((language: ILanguage) => ({
        label: this.translationService.translateWithLanguageProvided(
          `${memberDirectFeatureLocalePrefix}.languageNames`,
          language.code
        ),
        value: language.code,
      }));
  }

  private updateLogoAltText(language: string): void {
    this.logoAltText = this.surveyConfigService.getLogoAltText(language);
  }

  printPage(): void {
    window.print();
  }
}
